import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/SocketContext';
import { formatDistanceToNow } from 'date-fns';
import {
    ArrowLeft, Phone, Mail, Edit2, Send, Plus,
    BellOff, FileText, Trash2, Clock, MessageSquare, Bot, Download, File,
    Image as ImageIcon, MapPin, X, Paperclip, Video as VideoIcon, ZoomOut, ZoomIn, ChevronDown
} from 'lucide-react';
import {
    getleadDetails, updatelead, addLeadNotes, markLeadAsRead,
    uploadWhatsAppLeadMedia, getLeadMessageMediaUrl, getStaffInfo,
    convertLeadToMember,
    sendMessage as postConversationMessage
} from '../../services/apiServices';
import LeftPane from '../../components/LeftPane';
import Header from '../../components/Header';
import '../styles/LeadDetail.css';
import QuoteModal from '../components/Quotepopup.js';
import LeadShareLocationModal from '../components/LeadShareLocationModal.js';

function staffDisplayName(s) {
    if (!s) return '';
    const n = (s.name || '').trim();
    if (n) return n;
    const fn = (s.firstname || '').trim();
    const ln = (s.lastname || '').trim();
    const full = `${fn} ${ln}`.trim();
    if (full) return full;
    if ((s.username || '').trim()) return s.username.trim();
    if ((s.email || '').trim()) return s.email.trim();
    return '';
}

/**
 * Load image / video / document from our API proxy (Bearer auth; same pattern as images).
 */
function LeadChatFetchedMedia({ messageId, variant, fileName, onImageClick }) {
    const [state, setState] = useState({
        blobUrl: null,
        mime: '',
        err: false,
        loading: true
    });

    useEffect(() => {
        let objectUrl;
        let cancelled = false;
        const token = localStorage.getItem('token');
        fetch(getLeadMessageMediaUrl(messageId), {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
            .then((r) => {
                if (!r.ok) throw new Error('load failed');
                return r.blob();
            })
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setState({
                    blobUrl: objectUrl,
                    mime: blob.type || '',
                    err: false,
                    loading: false
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setState({ blobUrl: null, mime: '', err: true, loading: false });
                }
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [messageId]);

    if (state.loading) {
        return <span className="leaddetail-image-placeholder">Loading…</span>;
    }
    if (state.err || !state.blobUrl) {
        return <span className="leaddetail-image-fallback">Unable to load file</span>;
    }

    if (variant === 'image') {
        const img = (
            <img
                src={state.blobUrl}
                alt=""
                className={`leaddetail-chat-image${onImageClick ? ' leaddetail-chat-image--clickable' : ''}`}
            />
        );
        if (onImageClick) {
            return (
                <button
                    type="button"
                    className="leaddetail-chat-image-trigger"
                    onClick={() => onImageClick(state.blobUrl)}
                    aria-label="View image full size"
                >
                    {img}
                </button>
            );
        }
        return img;
    }
    if (variant === 'video') {
        return (
            <video
                src={state.blobUrl}
                controls
                playsInline
                className="leaddetail-chat-video"
            />
        );
    }

    const safeName = fileName || 'document';
    const isPdf =
        state.mime === 'application/pdf' || safeName.toLowerCase().endsWith('.pdf');
    return (
        <div className="leaddetail-doc-preview-wrap">
            {isPdf ? (
                <iframe title={safeName} src={state.blobUrl} className="leaddetail-doc-iframe" />
            ) : null}
            <div className="leaddetail-doc-view-row">
                <a
                    href={state.blobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="leaddetail-quote-download"
                >
                    Open in new tab
                </a>
                <a
                    href={state.blobUrl}
                    download={safeName}
                    className="leaddetail-quote-download"
                >
                    <Download size={14} /> Save
                </a>
            </div>
        </div>
    );
}

const LeadDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const gymId = JSON.parse(localStorage.getItem("loggedUserInfo")).gymid;
    const { socket, isConnected, connect: connectSocket } = useSocket();
    const [showQuote, setShowQuote] = useState(false);
    const [lead, setLead] = useState(null);
    const [messages, setMessages] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msgInput, setMsgInput] = useState('');
    const [commentInput, setCommentInput] = useState('');
    const [editingPrefs, setEditingPrefs] = useState(false);
    const [editingBasicInfo, setEditingBasicInfo] = useState(false);
    const [prefs, setPrefs] = useState({ preferredTime: '', budget: '', timeline: '', address: '' });
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [staffOptions, setStaffOptions] = useState([]);
    const [staffListLoading, setStaffListLoading] = useState(false);
    const [basicInfo, setBasicInfo] = useState({
        name: '',
        age: '',
        leadNumber: '',
        gender: '',
        source: '',
        assignedTo: '',
        email: "",
        phone: ''
    });
    const [status, setStatus] = useState('Lead');
    const [priority, setPriority] = useState('Hot');
    const [dnd, setDnd] = useState(false);
    const [sending, setSending] = useState(false);
    const chatScrollRef = useRef(null);
    const chatContentMeasureRef = useRef(null);
    /** When true, new messages snap scroll to bottom; set false when user scrolls up */
    const stickToBottomRef = useRef(true);
    const [showJumpToLatest, setShowJumpToLatest] = useState(false);
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const documentInputRef = useRef(null);
    const mediaMenuRef = useRef(null);
    const [tempMessages, setTempMessages] = useState({});
    const processedMessageIds = useRef(new Set());
    /** { kind: 'image'|'video'|'document', mediaId, fileName, previewUrl?, uploading } */
    const [pendingMedia, setPendingMedia] = useState(null);
    const [showMediaMenu, setShowMediaMenu] = useState(false);
    const [locationDraft, setLocationDraft] = useState(null);
    /** Full-screen image viewer: { src, w?, h? } natural dimensions set on load for zoom layout */
    const [imageLightbox, setImageLightbox] = useState(null);
    const [lightboxZoom, setLightboxZoom] = useState(1);

    const openImageLightbox = useCallback((src) => {
        if (!src) return;
        setLightboxZoom(1);
        setImageLightbox({ src, w: null, h: null });
    }, []);

    const closeImageLightbox = useCallback(() => {
        setImageLightbox(null);
        setLightboxZoom(1);
    }, []);

    useEffect(() => {
        if (!imageLightbox) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeImageLightbox();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [imageLightbox, closeImageLightbox]);

    useEffect(() => {
        if (!imageLightbox) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [imageLightbox]);

    useEffect(() => {
        if (!showMediaMenu) return;
        const close = (e) => {
            if (mediaMenuRef.current && !mediaMenuRef.current.contains(e.target)) {
                setShowMediaMenu(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showMediaMenu]);

    useEffect(() => {
        if (!gymId) return;
        let cancelled = false;
        setStaffListLoading(true);
        (async () => {
            try {
                const res = await getStaffInfo(gymId);
                if (cancelled) return;
                if (!res || res === 'false' || !res.ok) {
                    setStaffOptions([]);
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                const names = [...new Set(list.map(staffDisplayName).filter(Boolean))];
                names.sort((a, b) => a.localeCompare(b));
                setStaffOptions(names);
            } catch {
                if (!cancelled) setStaffOptions([]);
            } finally {
                if (!cancelled) setStaffListLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [gymId]);

    const assignedStaffSelectOptions = useMemo(() => {
        const set = new Set(staffOptions);
        const cur = (basicInfo.assignedTo || '').trim();
        if (cur && cur !== 'Unassigned') set.add(cur);
        return [...set].sort((a, b) => a.localeCompare(b));
    }, [staffOptions, basicInfo.assignedTo]);

    // ── Check if WhatsApp 24hr session is expired ──────────────────────────
    const isSessionExpired = () => {
        if (!messages || messages.length === 0) return true;
        const lastMsg = [...messages].sort((a, b) =>
            new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)
        )[0];
        const lastMsgTime = new Date(lastMsg.timestamp || lastMsg.createdAt);
        const hoursDiff = (Date.now() - lastMsgTime.getTime()) / (1000 * 60 * 60);
        return hoursDiff >= 24;
    };

    const markAsRead = async () => {
        const res = await markLeadAsRead(id);
        if (!res || res === 'false') return;
        const resjson = await res.json();
    };

    // ── Fetch lead details ─────────────────────────────────────────────────
    useEffect(() => {
        const fetchLead = async () => {
            try {
                setLoading(true);
                const res = await getleadDetails(id);
                if (!res || res === 'false') return;
                const json = await res.json();
                console.log(json, 'data');

                const { lead: l, messages: msgs } = json.data || {};
                console.log(l, 'l');

                if (l) {
                    setLead(l);
                    setStatus(l.status || 'Lead');
                    setPriority(l.priority || 'Warm');
                    setDnd(l.doNotDisturb || false);
                    setPrefs({
                        preferredTime: l.preferredTime || '',
                        budget: l.budget || '',
                        timeline: l.timeline || '',
                        address: l.address || '',
                    });
                    setBasicInfo({
                        name: l.name || '',
                        age: l.age || '',
                        leadNumber: l.leadNumber || l.leadId || '',
                        gender: l.gender || '',
                        source: l.source || '',
                        assignedTo: l.assignedTo || '',
                        email: l.email || "",
                        phone: l.phone || ''
                    });
                    if (l.notes?.length) setActivities(l.notes);
                }
                if (msgs?.length) {
                    setMessages(msgs);
                    msgs.forEach(msg => {
                        if (msg._id) processedMessageIds.current.add(msg._id);
                        if (msg.whatsappMessageId) processedMessageIds.current.add(msg.whatsappMessageId);
                        if (msg.messageId) processedMessageIds.current.add(msg.messageId);
                    });
                }

                if (l && !l.conversationId) {
                    console.warn('[WA client] lead has no conversationId — cannot join conversation room');
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        if (id) {
            fetchLead();
            markAsRead();
        }

        return () => {
            processedMessageIds.current.clear();
        };
    }, [id]);

    /** Join rooms after lead loads and whenever the socket (re)connects — avoids lost emits when connected was false. */
    useEffect(() => {
        if (!socket || !lead || String(lead._id) !== String(id)) return;

        const doJoin = () => {
            if (!socket.connected) return;
            if (lead.conversationId) {
                socket.emit('join_conversation', lead.conversationId);
            }
            socket.emit('join_lead', id);
        };

        doJoin();
        socket.on('connect', doJoin);
        return () => {
            socket.off('connect', doJoin);
            if (lead.conversationId) {
                socket.emit('leave_conversation', lead.conversationId);
            }
            socket.emit('leave_lead', id);
        };
    }, [socket, lead, id]);

    useEffect(() => {
        stickToBottomRef.current = true;
    }, [id]);

    const BOTTOM_SCROLL_THRESHOLD = 80;

    const updateScrollStickiness = useCallback(() => {
        const el = chatScrollRef.current;
        if (!el) return;
        const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
        const nearBottom = gap <= BOTTOM_SCROLL_THRESHOLD;
        stickToBottomRef.current = nearBottom;
        setShowJumpToLatest(!nearBottom && messages.length > 0);
    }, [messages.length]);

    const scrollChatToBottom = useCallback((smooth) => {
        const el = chatScrollRef.current;
        if (!el) return;
        stickToBottomRef.current = true;
        setShowJumpToLatest(false);
        const target = el.scrollHeight;
        if (smooth) {
            el.scrollTo({ top: target, behavior: 'smooth' });
        } else {
            el.scrollTop = target;
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
                updateScrollStickiness();
            });
        }
    }, [updateScrollStickiness]);

    // Snap to latest when opening lead or when new messages arrive while user is at bottom
    useEffect(() => {
        const el = chatScrollRef.current;
        if (!el || messages.length === 0) {
            setShowJumpToLatest(false);
            return;
        }
        if (!stickToBottomRef.current) return;
        const run = () => {
            el.scrollTop = el.scrollHeight;
            setShowJumpToLatest(false);
        };
        run();
        requestAnimationFrame(run);
    }, [messages]);

    // ── Helper: add message with duplicate prevention ──────────────────────
    const addMessageIfNotExists = (newMessage) => {
        if (!newMessage) return false;

        const messageId = newMessage._id;
        const whatsappId = newMessage.whatsappMessageId;
        const regularId = newMessage.messageId;

        if (messageId && processedMessageIds.current.has(messageId)) return false;
        if (whatsappId && processedMessageIds.current.has(whatsappId)) return false;
        if (regularId && processedMessageIds.current.has(regularId)) return false;

        const existsInState = messages.some(msg =>
            msg._id === messageId ||
            msg.whatsappMessageId === whatsappId ||
            msg.messageId === regularId
        );
        if (existsInState) return false;

        if (messageId) processedMessageIds.current.add(messageId);
        if (whatsappId) processedMessageIds.current.add(whatsappId);
        if (regularId) processedMessageIds.current.add(regularId);

        setMessages(prev => [...prev, newMessage]);
        return true;
    };

    // ── Socket event listeners ─────────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = (data) => {
            const msg = data?.message;
            console.log('[WA client RECV] new_message', {
                success: data?.success,
                isUpdate: data?.isUpdate,
                tempMessageId: data?.tempMessageId,
                dbId: msg?._id,
                type: msg?.type,
                sender: msg?.sender,
                status: msg?.status
            });
            if (!data?.success || !msg) return;

            if (data.isUpdate && msg._id) {
                console.log('[WA client RECV] new_message → merge update', String(msg._id));
                setMessages((prev) =>
                    prev.map((m) => (m._id === msg._id ? { ...m, ...msg } : m))
                );
                return;
            }

            if (data.tempMessageId) {
                console.log('[WA client RECV] new_message → replace temp', data.tempMessageId, 'with', String(msg._id));
                setMessages((prev) => {
                    const oldTemp = prev.find((m) => m._id === data.tempMessageId);
                    if (oldTemp?.localPreviewUrl) {
                        URL.revokeObjectURL(oldTemp.localPreviewUrl);
                    }
                    const withoutTemp = prev.filter((m) => m._id !== data.tempMessageId);
                    if (withoutTemp.some((m) => m._id === msg._id)) return withoutTemp;
                    if (msg._id) processedMessageIds.current.add(msg._id);
                    return [...withoutTemp, msg];
                });
                return;
            }

            const added = addMessageIfNotExists(msg);
            console.log('[WA client RECV] new_message → append if new:', added);
        };

        const handleMessageError = (data) => {
            console.warn('[WA client RECV] message_error', data);
            const tid = data?.tempMessageId;
            if (!tid) return;
            setMessages((prev) =>
                prev.map((m) =>
                    m._id === tid ? { ...m, status: 'failed', error: data.error } : m
                )
            );
        };

        const handleMemberMessageReceived = (data) => {
            const m = data?.message;
            console.log('[WA client RECV] member_message_received', {
                success: data?.success,
                dbId: m?._id,
                type: m?.type,
                conversationId: data?.conversationId
            });
            if (data.success && m) addMessageIfNotExists(m);
        };

        const handleLeadMessageReceived = (data) => {
            const m = data?.message;
            console.log('[WA client RECV] lead_message_received', {
                leadId: data?.leadId,
                dbId: m?._id,
                type: m?.type
            });
            if (m) addMessageIfNotExists(m);
        };

        socket.on('new_message', handleNewMessage);
        socket.on('member_message_received', handleMemberMessageReceived);
        socket.on('lead_message_received', handleLeadMessageReceived);
        socket.on('message_error', handleMessageError);

        socket.on('message_status_updated', (data) => {
            const { dbMessageId, whatsappMessageId, status } = data;
            console.log('[WA client RECV] message_status_updated', {
                dbMessageId,
                whatsappMessageId,
                status
            });
            setMessages(prevMessages =>
                prevMessages.map(msg => {
                    if (msg._id === dbMessageId) return { ...msg, status };
                    if (msg.whatsappMessageId === whatsappMessageId || msg.messageId === whatsappMessageId) return { ...msg, status };
                    if (msg.messageId === dbMessageId || msg.messageId === whatsappMessageId) return { ...msg, status };
                    return msg;
                })
            );
            if (tempMessages[dbMessageId]) {
                setTempMessages(prev => {
                    const newTemp = { ...prev };
                    delete newTemp[dbMessageId];
                    return newTemp;
                });
            }
        });

        return () => {
            socket.off('new_message', handleNewMessage);
            socket.off('member_message_received', handleMemberMessageReceived);
            socket.off('lead_message_received', handleLeadMessageReceived);
            socket.off('message_error', handleMessageError);
            socket.off('message_status_updated');
        };
    }, [socket, tempMessages, messages]);

    // ── Download quote PDF ─────────────────────────────────────────────────
    const downloadQuote = async (mediaUrl, fileName) => {
        try {
            if (mediaUrl) {
                window.open(mediaUrl, '_blank');
            } else {
                const response = await fetch('/api/quotes/download', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: mediaUrl })
                });
                const data = await response.json();
                if (data.url) window.open(data.url, '_blank');
            }
        } catch (error) {
            console.error('Error downloading quote:', error);
        }
    };

    const staffname = JSON.parse(localStorage.getItem("loggedUserInfo")).name;
    const staffid = JSON.parse(localStorage.getItem("loggedUserInfo"))._id;

    const markTempMessageFailed = (tempId) => {
        if (!tempId) return;
        setMessages((prev) =>
            prev.map((msg) =>
                msg._id === tempId ? { ...msg, status: 'failed' } : msg
            )
        );
    };

    /**
     * Briefly try to bring the socket up (Socket.IO may be disconnected after navigation).
     * Then either emit or, for plain text, POST to the same API as WhatsAppMessages.
     */
    const waitForSocketReady = (maxMs = 1200) =>
        new Promise((resolve) => {
            if (!socket) {
                resolve(false);
                return;
            }
            if (socket.connected) {
                resolve(true);
                return;
            }
            try {
                connectSocket?.();
            } catch (_) {
                /* ignore */
            }
            const finish = (ok) => {
                clearTimeout(tid);
                try {
                    socket.off('connect', onConnect);
                } catch (_) {
                    /* ignore */
                }
                resolve(ok);
            };
            const onConnect = () => finish(true);
            socket.once('connect', onConnect);
            const tid = setTimeout(() => finish(!!socket.connected), maxMs);
        });

    const sendTextViaHttp = async (payload, tempId) => {
        try {
            const response = await postConversationMessage(
                String(payload.conversationId),
                String(payload.text).trim(),
                { tempMessageId: tempId }
            );
            if (!response || response === false) {
                console.warn('[WA client SEND] HTTP send: no response');
                markTempMessageFailed(tempId);
                return;
            }
            if (!response.ok) {
                let errBody = '';
                try {
                    errBody = await response.clone().text();
                } catch (_) {
                    /* ignore */
                }
                console.warn('[WA client SEND] HTTP send failed', response.status, errBody);
                markTempMessageFailed(tempId);
                return;
            }
            const responseData = await response.json();
            if (responseData?.success && responseData.data) {
                const finalMessage = responseData.data;
                if (finalMessage._id) processedMessageIds.current.add(finalMessage._id);
                setMessages((prev) => {
                    const filtered = prev.filter((m) => m._id !== tempId);
                    const exists = filtered.some(
                        (m) =>
                            m._id === finalMessage._id ||
                            m.messageId === finalMessage.messageId ||
                            m.whatsappMessageId === finalMessage.whatsappMessageId
                    );
                    if (exists) return filtered;
                    return [...filtered, finalMessage];
                });
                setTempMessages((prev) => {
                    const next = { ...prev };
                    delete next[tempId];
                    return next;
                });
                console.log('[WA client SEND] HTTP send OK, replaced temp', tempId);
            } else {
                console.warn('[WA client SEND] HTTP unexpected JSON', responseData);
                markTempMessageFailed(tempId);
            }
        } catch (err) {
            console.error('[WA client SEND] HTTP send error', err);
            markTempMessageFailed(tempId);
        }
    };

    const dispatchOutboundMessage = async (payload) => {
        const tempId = payload.tempMessageId;
        const t = payload.type || 'text';
        const isText =
            t === 'text' && payload.text != null && String(payload.text).trim() !== '';

        console.log('[WA client SEND] dispatchOutboundMessage', {
            socketConnected: !!socket?.connected,
            isConnected,
            socketId: socket?.id,
            type: t,
            conversationId: payload.conversationId ? String(payload.conversationId) : null,
            tempMessageId: tempId,
            textLen: (payload.text || '').length,
            hasMediaId: !!payload.mediaId
        });

        // Connected: realtime path (fastest).
        if (socket?.connected) {
            try {
                socket.emit('send_message', payload);
            } catch (err) {
                console.error('[WA client SEND] emit threw', err);
                if (isText) {
                    await sendTextViaHttp(payload, tempId);
                } else {
                    markTempMessageFailed(tempId);
                }
            }
            return;
        }

        // Not connected: kick reconnect for the next action (non-blocking).
        try {
            connectSocket?.();
        } catch (_) {
            /* ignore */
        }

        // Text: send via REST immediately — do not wait on socket (fixes first message before connect).
        if (isText) {
            await sendTextViaHttp(payload, tempId);
            return;
        }

        // Media / location: wait briefly for socket only (no HTTP fallback).
        const ok = socket ? await waitForSocketReady(2500) : false;
        if (ok && socket?.connected) {
            try {
                socket.emit('send_message', payload);
            } catch (err) {
                console.error('[WA client SEND] emit threw', err);
                markTempMessageFailed(tempId);
            }
            return;
        }

        console.warn('[WA client SEND] socket not connected — no HTTP fallback for type', t);
        markTempMessageFailed(tempId);
    };

    // ── Send message (text, pending image + caption, or location draft) ──
    const handleSend = async () => {
        if (sending) {
            console.log('[WA client SEND] handleSend skipped: already sending');
            return;
        }
        if (!lead?.conversationId) {
            console.warn('[WA client SEND] handleSend blocked: lead has no conversationId', {
                leadId: id,
                leadKeys: lead ? Object.keys(lead) : []
            });
            return;
        }
        if (pendingMedia?.uploading) {
            console.log('[WA client SEND] handleSend skipped: media still uploading');
            return;
        }

        if (locationDraft) {
            const { latitude, longitude, name, address } = locationDraft;
            if (latitude == null || longitude == null) return;

            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const tempMsg = {
                _id: tempId,
                text: name || address || 'Shared location',
                sender: 'business',
                staffName: staffname,
                staffId: staffid,
                timestamp: new Date().toISOString(),
                status: 'sending',
                isTemporary: true,
                type: 'location',
                latitude,
                longitude,
                locationName: name || '',
                locationAddress: address || ''
            };
            setMessages((p) => [...p, tempMsg]);
            setTempMessages((prev) => ({ ...prev, [tempId]: true }));
            setLocationDraft(null);
            setSending(true);
            try {
                await dispatchOutboundMessage({
                    conversationId: lead.conversationId,
                    type: 'location',
                    text: '',
                    latitude,
                    longitude,
                    locationName: name || undefined,
                    locationAddress: address || undefined,
                    gymId,
                    tempMessageId: tempId,
                    staffId: staffid,
                    staffName: staffname,
                    leadId: id
                });
            } finally {
                setSending(false);
            }
            return;
        }

        if (pendingMedia?.mediaId) {
            const caption = msgInput.trim();
            const { mediaId, fileName, previewUrl, kind } = pendingMedia;
            const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const tempMsg = {
                _id: tempId,
                text: caption,
                sender: 'business',
                staffName: staffname,
                staffId: staffid,
                timestamp: new Date().toISOString(),
                status: 'sending',
                isTemporary: true,
                type: kind,
                mediaId,
                fileName,
                localPreviewUrl: previewUrl
            };
            setMessages((p) => [...p, tempMsg]);
            setTempMessages((prev) => ({ ...prev, [tempId]: true }));
            setMsgInput('');
            setPendingMedia(null);
            setSending(true);
            try {
                await dispatchOutboundMessage({
                    conversationId: lead.conversationId,
                    type: kind,
                    text: caption,
                    mediaId,
                    fileName,
                    gymId,
                    tempMessageId: tempId,
                    staffId: staffid,
                    staffName: staffname,
                    leadId: id
                });
            } finally {
                setSending(false);
            }
            return;
        }

        if (!msgInput.trim()) {
            console.log('[WA client SEND] handleSend skipped: empty text (no media/location pending)');
            return;
        }

        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        console.log('[WA client SEND] handleSend TEXT branch', {
            tempId,
            conversationId: String(lead.conversationId)
        });
        const tempMsg = {
            _id: tempId,
            text: msgInput,
            sender: 'business',
            staffName: staffname,
            staffId: staffid,
            timestamp: new Date().toISOString(),
            status: 'sending',
            isTemporary: true,
            type: 'text'
        };

        setMessages((p) => [...p, tempMsg]);
        setTempMessages((prev) => ({ ...prev, [tempId]: true }));

        const text = msgInput;
        setMsgInput('');
        setSending(true);

        try {
            const textPayload = {
                conversationId: lead?.conversationId,
                text,
                gymId,
                tempMessageId: tempId,
                staffId: staffid,
                staffName: staffname,
                leadId: id,
                type: 'text'
            };
            await dispatchOutboundMessage(textPayload);
        } finally {
            setSending(false);
        }
    };

    const validateMediaFile = (kind, file) => {
        const t = (file.type || '').toLowerCase();
        if (kind === 'image') {
            if (!/^image\/(jpeg|png)$/i.test(t)) {
                alert('WhatsApp only accepts JPEG or PNG images.');
                return false;
            }
        } else if (kind === 'video') {
            if (!/^video\/(mp4|3gpp)$/i.test(t)) {
                alert('WhatsApp video: use MP4 or 3GPP only.');
                return false;
            }
        } else if (kind === 'document') {
            const ok =
                t === 'application/pdf' ||
                t === 'text/plain' ||
                t === 'application/msword' ||
                t === 'application/vnd.ms-excel' ||
                /^application\/vnd\.openxmlformats-officedocument\./i.test(t);
            if (!ok) {
                alert('Use PDF, Word, Excel, PowerPoint, or plain text files.');
                return false;
            }
        }
        return true;
    };

    const handleMediaFile = async (kind, e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        setShowMediaMenu(false);
        if (!file || !gymId) return;
        if (!validateMediaFile(kind, file)) return;

        const previewUrl =
            kind === 'image' || kind === 'video' ? URL.createObjectURL(file) : null;
        setPendingMedia({
            kind,
            mediaId: null,
            fileName: file.name,
            previewUrl,
            uploading: true
        });

        try {
            const res = await uploadWhatsAppLeadMedia(gymId, file);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(json.message || 'Upload failed');
            }
            const serverCat = json.data?.category;
            if (serverCat && serverCat !== kind) {
                throw new Error(`Server classified file as ${serverCat}; please pick another type.`);
            }
            setPendingMedia({
                kind,
                mediaId: json.data?.mediaId,
                fileName: json.data?.fileName || file.name,
                previewUrl,
                uploading: false
            });
        } catch (err) {
            console.error(err);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPendingMedia(null);
            alert(err.message || 'Could not upload file');
        }
    };

    const clearPendingMedia = () => {
        if (pendingMedia?.previewUrl) {
            URL.revokeObjectURL(pendingMedia.previewUrl);
        }
        setPendingMedia(null);
    };

    // ── Add comment ────────────────────────────────────────────────────────
    const handleAddComment = async () => {
        if (!commentInput.trim()) return;
        try {
            const res = await addLeadNotes(id, { text: commentInput, createdBy: 'Staff' });
            if (!res || res === 'false') return;
            const json = await res.json();
            if (json.success && json.data?.notes) setActivities(json.data.notes);
            setCommentInput('');
        } catch (err) {
            console.error(err);
        }
    };

    // ── Save preferences ───────────────────────────────────────────────────
    const handleSavePrefs = async () => {
        try {
            const res = await updatelead(id, { ...prefs });
            if (!res || res === 'false') return;
            setEditingPrefs(false);
        } catch (err) {
            console.error(err);
        }
    };

    // ── Save basic info ────────────────────────────────────────────────────
    const handleSaveBasicInfo = async () => {
        try {
            const payload = { ...basicInfo };
            if (payload.gender === '') delete payload.gender;
            if (payload.source === '') delete payload.source;
            const res = await updatelead(id, payload);
            if (!res || res === 'false') return;
            setEditingBasicInfo(false);
            setLead(prev => ({ ...prev, ...payload }));
        } catch (err) {
            console.error(err);
        }
    };

    // ── Update status/priority/dnd ─────────────────────────────────────────
    const handleUpdateActions = async (type, value) => {
        try {
            const updateObj = { status, priority, doNotDisturb: dnd };
            updateObj[type] = value;
            const res = await updatelead(id, updateObj);
            if (!res || res === 'false' || !res.ok) {
                const errJson = await res?.json?.().catch(() => ({}));
                alert(errJson.message || 'Update failed');
                return;
            }
            const json = await res.json();
            const updated = json.data;
            if (updated) {
                setLead((prev) => ({ ...prev, ...updated }));
                setStatus(updated.status);
                setPriority(updated.priority);
                setDnd(!!updated.doNotDisturb);
            } else {
                setStatus(updateObj.status);
                setPriority(updateObj.priority);
                setDnd(updateObj.doNotDisturb);
            }
        } catch (err) {
            console.error(err);
            alert(err.message || 'Update failed');
        }
    };

    const handleConvertToMember = async () => {
        if (lead?.memberId) return;
        if (!window.confirm(
            'Convert this lead to a member? A new member and member profile will be created in your gym.'
        )) return;
        try {
            const res = await convertLeadToMember(id);
            const json = await res?.json?.().catch(() => ({}));
            if (!res?.ok) {
                alert(json.message || 'Conversion failed');
                return;
            }
            if (json.data) {
                setLead((prev) => ({ ...prev, ...json.data }));
                setStatus(json.data.status || 'Member');
            } else {
                setStatus('Member');
                setLead((prev) => ({ ...prev, status: 'Member' }));
            }
        } catch (err) {
            console.error(err);
            alert(err.message || 'Conversion failed');
        }
    };

    // ── Delete lead ────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!window.confirm('Delete this lead? This cannot be undone.')) return;
        try {
            await fetch(`/api/leads/deletelead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ leadId: id, gymId })
            });
            navigate('/whatsapp/leads');
        } catch (err) {
            console.error(err);
        }
    };

    // ── Message status icon ────────────────────────────────────────────────
    const getStatusIcon = (status) => {
        switch (status) {
            case 'sending':   return <span className="leaddetail-status-sending">⏳</span>;
            case 'sent':      return <span className="leaddetail-status-sent">✓</span>;
            case 'delivered': return <span className="leaddetail-status-delivered">✓✓</span>;
            case 'read':      return <span className="leaddetail-status-read">✓✓</span>;
            case 'failed':    return <span className="leaddetail-status-failed">❌</span>;
            default:          return null;
        }
    };

    // ── Format file name ───────────────────────────────────────────────────
    const formatFileName = (fileName) => {
        if (!fileName) return 'Quote.pdf';
        return fileName.replace('.pdf', '');
    };

    // ── Sender label helper ────────────────────────────────────────────────
    // Shows staffName for business messages, "Chatbot" for bot, nothing for member
    const renderSenderLabel = (msg, isInbound) => {
        if (isInbound) return null;

        // Auto-reply / bot
        if (msg.sender === 'bot') {
            return (
                <div className="leaddetail-bubble-sender">
                    <Bot size={12} /> Chatbot
                </div>
            );
        }

        // Business message — show staff name if available
        if (msg.sender === 'business' && msg.staffName) {
            return (
                <div className="leaddetail-bubble-staff">
                    {msg.staffName}
                </div>
            );
        }

        // Any other non-member sender label
        if (msg.sender && msg.sender !== 'business') {
            return (
                <div className="leaddetail-bubble-sender">
                    {msg.sender}
                </div>
            );
        }

        return null;
    };

    // ── Render a single message bubble ─────────────────────────────────────
    const canFetchStoredMedia = (msg) =>
        msg._id &&
        !String(msg._id).startsWith('temp_') &&
        msg.mediaId;

    const renderMessage = (msg, isInbound) => {
        if (msg.type === 'image') {
            const showFetched = canFetchStoredMedia(msg);
            return (
                <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                    {renderSenderLabel(msg, isInbound)}
                    <div className="leaddetail-image-msg">
                        {msg.localPreviewUrl ? (
                            <button
                                type="button"
                                className="leaddetail-chat-image-trigger"
                                onClick={() => openImageLightbox(msg.localPreviewUrl)}
                                aria-label="View image full size"
                            >
                                <img
                                    src={msg.localPreviewUrl}
                                    alt=""
                                    className="leaddetail-chat-image leaddetail-chat-image--clickable"
                                />
                            </button>
                        ) : showFetched ? (
                            <LeadChatFetchedMedia
                                messageId={msg._id}
                                variant="image"
                                onImageClick={openImageLightbox}
                            />
                        ) : (
                            <span className="leaddetail-image-placeholder">Image</span>
                        )}
                        {msg.text ? (
                            <div className="leaddetail-bubble-text leaddetail-image-caption">{msg.text}</div>
                        ) : null}
                    </div>
                    <div className="leaddetail-bubble-time">
                        {formatMsgTime(msg.timestamp || msg.createdAt)}
                        {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                    </div>
                </div>
            );
        }

        if (msg.type === 'location') {
            const lat = msg.latitude;
            const lng = msg.longitude;
            const mapsUrl =
                lat != null && lng != null
                    ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}`
                    : null;
            return (
                <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                    {renderSenderLabel(msg, isInbound)}
                    <div className="leaddetail-location-msg">
                        <MapPin size={18} className="leaddetail-location-icon" />
                        <div>
                            {(msg.locationName || msg.locationAddress) && (
                                <div className="leaddetail-location-text">
                                    {msg.locationName ? <div>{msg.locationName}</div> : null}
                                    {msg.locationAddress ? <div>{msg.locationAddress}</div> : null}
                                </div>
                            )}
                            {mapsUrl && (
                                <a
                                    className="leaddetail-location-link"
                                    href={mapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open in Maps
                                </a>
                            )}
                            {!msg.locationName && !msg.locationAddress && !mapsUrl && (
                                <span className="leaddetail-location-text">Shared location</span>
                            )}
                        </div>
                    </div>
                    <div className="leaddetail-bubble-time">
                        {formatMsgTime(msg.timestamp || msg.createdAt)}
                        {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                    </div>
                </div>
            );
        }

        if (msg.type === 'video') {
            const name = msg.fileName || 'Video';
            const showFetched = canFetchStoredMedia(msg);
            return (
                <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                    {renderSenderLabel(msg, isInbound)}
                    <div className="leaddetail-video-msg leaddetail-video-msg-block">
                        <div className="leaddetail-video-msg-head">
                            <VideoIcon size={22} className="leaddetail-video-msg-icon" />
                            <div className="leaddetail-video-msg-name">{name}</div>
                        </div>
                        {msg.localPreviewUrl ? (
                            <video
                                src={msg.localPreviewUrl}
                                controls
                                playsInline
                                className="leaddetail-chat-video"
                            />
                        ) : showFetched ? (
                            <LeadChatFetchedMedia messageId={msg._id} variant="video" />
                        ) : (
                            <span className="leaddetail-image-placeholder">Video</span>
                        )}
                        {msg.text ? (
                            <div className="leaddetail-bubble-text leaddetail-image-caption">{msg.text}</div>
                        ) : null}
                    </div>
                    <div className="leaddetail-bubble-time">
                        {formatMsgTime(msg.timestamp || msg.createdAt)}
                        {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                    </div>
                </div>
            );
        }

        if (msg.type === 'document') {
            const fileName = msg.fileName || `file-${String(msg._id || '').slice(-6)}`;
            const displayName = formatFileName(fileName);
            const showFetched = canFetchStoredMedia(msg);
            return (
                <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                    {renderSenderLabel(msg, isInbound)}
                    <div className="leaddetail-quote-message leaddetail-doc-chat-card">
                        <div className="leaddetail-quote-icon">
                            <FileText size={24} />
                        </div>
                        <div className="leaddetail-quote-content">
                            <div className="leaddetail-quote-title">{displayName}</div>
                            {msg.text && msg.text !== `Quote ${displayName} sent` && (
                                <div className="leaddetail-quote-caption">{msg.text}</div>
                            )}
                            {msg.mediaUrl ? (
                                <button
                                    type="button"
                                    className="leaddetail-quote-download"
                                    onClick={() => downloadQuote(msg.mediaUrl, fileName)}
                                >
                                    <Download size={14} /> Download
                                </button>
                            ) : showFetched ? (
                                <LeadChatFetchedMedia
                                    messageId={msg._id}
                                    variant="document"
                                    fileName={fileName}
                                />
                            ) : (
                                <span className="leaddetail-doc-no-link">No file preview available</span>
                            )}
                        </div>
                    </div>
                    <div className="leaddetail-bubble-time">
                        {formatMsgTime(msg.timestamp || msg.createdAt)}
                        {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                    </div>
                </div>
            );
        }

        // Legacy rows with mediaUrl but non-standard type (e.g. older quote payloads)
        if (msg.mediaUrl && msg.type !== 'image' && msg.type !== 'video' && msg.type !== 'location') {
            const fileName = msg.fileName || `Quote-${msg._id.slice(-6)}.pdf`;
            const displayName = formatFileName(fileName);

            return (
                <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                    {renderSenderLabel(msg, isInbound)}
                    <div className="leaddetail-quote-message">
                        <div className="leaddetail-quote-icon">
                            <FileText size={24} />
                        </div>
                        <div className="leaddetail-quote-content">
                            <div className="leaddetail-quote-title">{displayName}</div>
                            {msg.text && msg.text !== `Quote ${displayName} sent` && (
                                <div className="leaddetail-quote-caption">{msg.text}</div>
                            )}
                            <button
                                type="button"
                                className="leaddetail-quote-download"
                                onClick={() => downloadQuote(msg.mediaUrl, fileName)}
                            >
                                <Download size={14} /> Download PDF
                            </button>
                        </div>
                    </div>
                    <div className="leaddetail-bubble-time">
                        {formatMsgTime(msg.timestamp || msg.createdAt)}
                        {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                    </div>
                </div>
            );
        }

        // Text message
        return (
            <div className={`leaddetail-bubble ${isInbound ? 'leaddetail-bubble-in' : 'leaddetail-bubble-out'}`}>
                {renderSenderLabel(msg, isInbound)}
                <div className="leaddetail-bubble-text">{msg.text}</div>
                <div className="leaddetail-bubble-time">
                    {formatMsgTime(msg.timestamp || msg.createdAt)}
                    {!isInbound && <span style={{ marginLeft: 4 }}>{getStatusIcon(msg.status)}</span>}
                </div>
            </div>
        );
    };

    // ── Badge helpers ──────────────────────────────────────────────────────
    const getStatusBadgeClass = (s) => ({
        Lead: 'leaddetail-tag-lead', Trial: 'leaddetail-tag-trial',
        Converted: 'leaddetail-tag-converted', Member: 'leaddetail-tag-member',
        Lost: 'leaddetail-tag-lost', 'Not Responded': 'leaddetail-tag-nr',
        'Quote Sent': 'leaddetail-tag-quote'
    }[s] || 'leaddetail-tag-lead');

    const getPriorityBadgeClass = (p) => ({
        Hot: 'leaddetail-tag-hot', Warm: 'leaddetail-tag-warm', Cold: 'leaddetail-tag-cold'
    }[p] || '');

    const formatMsgTime = (ts) => ts
        ? new Date(ts).toLocaleString('en-US', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        })
        : '';

    // ── Loading state ──────────────────────────────────────────────────────
    if (loading) return (
        <div className="leaddetail-root">
            <LeftPane isactive="16" />
            <Header />
            <div className="leaddetail-loading"><div className="leaddetail-spinner" /></div>
        </div>
    );

    const isLinkedGymMember = !!(lead?.memberId);

    return (
        <div className="leaddetail-root">
            <LeftPane isactive="16" />
            <Header />
            <div className="leaddetail-main gym-main rightContent">

                <button className="leaddetail-back" onClick={() => navigate('/whatsapp/leads')}>
                    <ArrowLeft size={15} /> Back to Leads
                </button>

                <div className="leaddetail-body">

                    {/* ══════ LEFT COLUMN ════════════════════════════════════ */}
                    <div className="leaddetail-left">

                        {/* Messages card */}
                        <div className="leaddetail-card leaddetail-messages-card">
                            <div className="leaddetail-card-title">Messages</div>

                            <div className="leaddetail-chat-scroll-wrap">
                                <div
                                    ref={chatScrollRef}
                                    className="leaddetail-chat-scroll"
                                    onScroll={updateScrollStickiness}
                                >
                                    <div ref={chatContentMeasureRef} className="leaddetail-chat-scroll-inner">
                                        {messages.map((msg, i) => {
                                            const isInbound = msg.sender === 'member';
                                            return (
                                                <div
                                                    key={msg._id || msg.whatsappMessageId || `${msg.text}_${msg.timestamp}_${i}`}
                                                    className={`leaddetail-msg-row ${isInbound ? 'leaddetail-msg-in' : 'leaddetail-msg-out'}`}
                                                >
                                                    {renderMessage(msg, isInbound)}
                                                </div>
                                            );
                                        })}
                                        {messages.length === 0 && (
                                            <div className="leaddetail-no-messages">
                                                <MessageSquare size={28} />
                                                <p>No messages yet</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {showJumpToLatest && (
                                    <button
                                        type="button"
                                        className="leaddetail-chat-jump-latest"
                                        onClick={() => scrollChatToBottom(true)}
                                        title="Latest messages"
                                        aria-label="Jump to latest messages"
                                    >
                                        <ChevronDown size={22} strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>

                            {isSessionExpired() ? (
                                <div className="leaddetail-session-expired">
                                    <div className="leaddetail-session-expired-inner">
                                        <span className="leaddetail-session-expired-title">
                                            🔒 Cannot send messages
                                        </span>
                                        <span className="leaddetail-session-expired-desc">
                                            The chat session is closed due to 24 hours of inactivity.
                                            It will now open only when the recipient sends a new message.
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="leaddetail-msg-compose">
                                    <input
                                        ref={imageInputRef}
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png"
                                        className="leaddetail-hidden-file"
                                        onChange={(e) => handleMediaFile('image', e)}
                                    />
                                    <input
                                        ref={videoInputRef}
                                        type="file"
                                        accept="video/mp4,video/3gpp"
                                        className="leaddetail-hidden-file"
                                        onChange={(e) => handleMediaFile('video', e)}
                                    />
                                    <input
                                        ref={documentInputRef}
                                        type="file"
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.*"
                                        className="leaddetail-hidden-file"
                                        onChange={(e) => handleMediaFile('document', e)}
                                    />
                                    {pendingMedia && (
                                        <div className="leaddetail-pending-image">
                                            {pendingMedia.kind === 'image' && pendingMedia.previewUrl && (
                                                <img src={pendingMedia.previewUrl} alt="" className="leaddetail-pending-thumb" />
                                            )}
                                            {pendingMedia.kind === 'video' && pendingMedia.previewUrl && (
                                                <video
                                                    src={pendingMedia.previewUrl}
                                                    className="leaddetail-pending-thumb leaddetail-pending-video"
                                                    muted
                                                    playsInline
                                                />
                                            )}
                                            {pendingMedia.kind === 'document' && (
                                                <div className="leaddetail-pending-doc-icon">
                                                    <FileText size={28} />
                                                </div>
                                            )}
                                            <span className="leaddetail-pending-label">
                                                {pendingMedia.uploading
                                                    ? 'Uploading…'
                                                    : `${pendingMedia.kind === 'image' ? 'Photo' : pendingMedia.kind === 'video' ? 'Video' : 'Document'} ready — caption optional`}
                                            </span>
                                            <button
                                                type="button"
                                                className="leaddetail-pending-clear"
                                                onClick={clearPendingMedia}
                                                aria-label="Remove attachment"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    )}
                                    {locationDraft && (
                                        <div className="leaddetail-location-draft">
                                            <div className="leaddetail-location-draft-title">Share this location?</div>
                                            <input
                                                className="leaddetail-msg-input leaddetail-location-input"
                                                placeholder="Place name (optional)"
                                                value={locationDraft.name}
                                                onChange={(e) =>
                                                    setLocationDraft((d) => ({ ...d, name: e.target.value }))
                                                }
                                            />
                                            <input
                                                className="leaddetail-msg-input leaddetail-location-input"
                                                placeholder="Address (optional)"
                                                value={locationDraft.address}
                                                onChange={(e) =>
                                                    setLocationDraft((d) => ({ ...d, address: e.target.value }))
                                                }
                                            />
                                            <div className="leaddetail-location-draft-actions">
                                                <button
                                                    type="button"
                                                    className="leaddetail-cancel-btn"
                                                    onClick={() => setLocationDraft(null)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="leaddetail-send-btn"
                                                    onClick={handleSend}
                                                >
                                                    <MapPin size={14} /> Send location
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div className="leaddetail-msg-input-row">
                                        <div className="leaddetail-media-attach-wrap" ref={mediaMenuRef}>
                                            <button
                                                type="button"
                                                className="leaddetail-attach-btn"
                                                title="Attach media"
                                                onClick={() => setShowMediaMenu((v) => !v)}
                                                disabled={
                                                    !!pendingMedia?.uploading ||
                                                    !!locationDraft ||
                                                    showLocationModal
                                                }
                                            >
                                                <Paperclip size={18} />
                                            </button>
                                            {showMediaMenu && (
                                                <div className="leaddetail-media-menu" role="menu">
                                                    <button
                                                        type="button"
                                                        className="leaddetail-media-menu-item"
                                                        onClick={() => {
                                                            setShowMediaMenu(false);
                                                            imageInputRef.current?.click();
                                                        }}
                                                    >
                                                        <ImageIcon size={16} /> Photo
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="leaddetail-media-menu-item"
                                                        onClick={() => {
                                                            setShowMediaMenu(false);
                                                            videoInputRef.current?.click();
                                                        }}
                                                    >
                                                        <VideoIcon size={16} /> Video
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="leaddetail-media-menu-item"
                                                        onClick={() => {
                                                            setShowMediaMenu(false);
                                                            documentInputRef.current?.click();
                                                        }}
                                                    >
                                                        <FileText size={16} /> Document
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="leaddetail-attach-btn"
                                            title="Share location (map or GPS)"
                                            onClick={() => setShowLocationModal(true)}
                                            disabled={!!pendingMedia || !!locationDraft || showLocationModal}
                                        >
                                            <MapPin size={18} />
                                        </button>
                                        <input
                                            className="leaddetail-msg-input"
                                            placeholder={
                                                pendingMedia?.mediaId
                                                    ? 'Caption (optional)…'
                                                    : 'Type your message…'
                                            }
                                            value={msgInput}
                                            onChange={(e) => setMsgInput(e.target.value)}
                                            onKeyDown={(e) =>
                                                e.key === 'Enter' && !e.shiftKey && handleSend()
                                            }
                                            disabled={!!locationDraft || showLocationModal}
                                        />
                                        <button
                                            className="leaddetail-send-btn"
                                            onClick={handleSend}
                                            disabled={
                                                sending ||
                                                !!pendingMedia?.uploading ||
                                                !!locationDraft ||
                                                showLocationModal ||
                                                (!msgInput.trim() && !pendingMedia?.mediaId)
                                            }
                                        >
                                            <Send size={15} /> Send
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Activity & Comments card */}
                        <div className="leaddetail-card leaddetail-activity-card">
                            <div className="leaddetail-card-title">Activity &amp; Comments</div>

                            <div className="leaddetail-activity-list">
                                {activities.map((note, i) => (
                                    <div key={note._id || i} className="leaddetail-activity-item">
                                        <div className="leaddetail-activity-icon">
                                            <MessageSquare size={13} />
                                        </div>
                                        <div className="leaddetail-activity-content">
                                            <div className="leaddetail-activity-meta">
                                                <span className="leaddetail-activity-author">
                                                    {note.createdBy || 'Staff'}
                                                </span>
                                                <span className="leaddetail-activity-time">
                                                    {note.createdAt
                                                        ? new Date(note.createdAt).toLocaleString('en-US', {
                                                            year: 'numeric', month: '2-digit', day: '2-digit',
                                                            hour: '2-digit', minute: '2-digit', hour12: false
                                                        })
                                                        : ''}
                                                </span>
                                            </div>
                                            <div className="leaddetail-activity-text">{note.text}</div>
                                        </div>
                                    </div>
                                ))}
                                {activities.length === 0 && (
                                    <p className="leaddetail-no-activity">No activity yet</p>
                                )}
                            </div>

                            <textarea
                                className="leaddetail-comment-input"
                                placeholder="Add a note or comment..."
                                value={commentInput}
                                onChange={e => setCommentInput(e.target.value)}
                                rows={3}
                            />
                            <button
                                className="leaddetail-comment-btn"
                                onClick={handleAddComment}
                                disabled={!commentInput.trim()}
                            >
                                <Plus size={14} /> Add Comment
                            </button>
                        </div>
                    </div>

                    {/* ══════ RIGHT COLUMN ═══════════════════════════════════ */}
                    <div className="leaddetail-right">

                        {/* Lead info card */}
                        <div className="leaddetail-card leaddetail-info-card">
                            <div className="leaddetail-info-header">
                                <div>
                                    <div className="leaddetail-lead-name">
                                        {!editingBasicInfo ? (
                                            lead?.name
                                        ) : (
                                            <input
                                                className="leaddetail-edit-input"
                                                value={basicInfo.name}
                                                onChange={e => setBasicInfo(prev => ({ ...prev, name: e.target.value }))}
                                                placeholder="Name"
                                            />
                                        )}
                                    </div>
                                    <div className="leaddetail-lead-id">
                                        {!editingBasicInfo ? (
                                            `ID #${lead?.leadNumber || lead?.leadId || lead?._id?.slice(-4)}`
                                        ) : (
                                            <input
                                                className="leaddetail-edit-input-small"
                                                value={basicInfo.leadNumber}
                                                onChange={e => setBasicInfo(prev => ({ ...prev, leadNumber: e.target.value }))}
                                                placeholder="Lead ID"
                                                size={10}
                                            />
                                        )}
                                    </div>
                                </div>
                                <button
                                    className="leaddetail-edit-icon"
                                    onClick={() => editingBasicInfo ? handleSaveBasicInfo() : setEditingBasicInfo(true)}
                                >
                                    <Edit2 size={15} />
                                </button>
                            </div>

                            {/* Status tags */}
                            <div className="leaddetail-tags-row">
                                <span className={`leaddetail-tag ${getStatusBadgeClass(lead?.status)}`}>
                                    {lead?.status}
                                </span>
                                {lead?.priority && (
                                    <span className={`leaddetail-tag ${getPriorityBadgeClass(lead?.priority)}`}>
                                        {lead?.priority}
                                    </span>
                                )}
                                {lead?.unreadCount > 0 && (
                                    <span className="leaddetail-tag leaddetail-tag-unread">Unread</span>
                                )}
                                {lead?.status === 'Not Responded' && (
                                    <span className="leaddetail-tag leaddetail-tag-nr-pill">Not Responded</span>
                                )}
                            </div>

                            {/* Contact info */}
                            <div className="leaddetail-contact-list">
                                {lead?.phone && (
                                    <div className="leaddetail-contact-row">
                                        <Phone size={14} className="leaddetail-contact-icon" />
                                        <span>
                                            {!editingBasicInfo ? (
                                                basicInfo.phone || '—'
                                            ) : (
                                                <input
                                                    className="leaddetail-edit-input-small"
                                                    type="number"
                                                    value={basicInfo.phone}
                                                    onChange={e => setBasicInfo(prev => ({ ...prev, phone: e.target.value }))}
                                                    placeholder="Phone"
                                                />
                                            )}
                                        </span>
                                    </div>
                                )}
                                {lead?.email && (
                                    <div className="leaddetail-contact-row">
                                        <Mail size={14} className="leaddetail-contact-icon" />
                                        <span>
                                            {!editingBasicInfo ? (
                                                basicInfo.email || '—'
                                            ) : (
                                                <input
                                                    className="leaddetail-edit-input-small"
                                                    type="text"
                                                    value={basicInfo.email}
                                                    onChange={e => setBasicInfo(prev => ({ ...prev, email: e.target.value }))}
                                                    placeholder="Email"
                                                />
                                            )}
                                        </span>
                                    </div>
                                )}
                                {lead?.channel && (
                                    <div className="leaddetail-contact-row">
                                        <Phone size={14} className="leaddetail-contact-icon" />
                                        <span>{lead.channel}</span>
                                    </div>
                                )}
                            </div>

                            {/* Meta fields */}
                            <div className="leaddetail-meta-table">
                                <div className="leaddetail-meta-row">
                                    <span className="leaddetail-meta-label">Age:</span>
                                    <span className="leaddetail-meta-value">
                                        {!editingBasicInfo ? (
                                            basicInfo.age || '—'
                                        ) : (
                                            <input
                                                className="leaddetail-edit-input-small"
                                                type="number"
                                                value={basicInfo.age}
                                                onChange={e => setBasicInfo(prev => ({ ...prev, age: e.target.value }))}
                                                placeholder="Age"
                                            />
                                        )}
                                    </span>
                                </div>

                                <div className="leaddetail-meta-row">
                                    <span className="leaddetail-meta-label">Gender:</span>
                                    <span className="leaddetail-meta-value">
                                        {!editingBasicInfo ? (
                                            basicInfo.gender || '—'
                                        ) : (
                                            <select
                                                className="leaddetail-edit-select"
                                                value={basicInfo.gender}
                                                onChange={e => setBasicInfo(prev => ({ ...prev, gender: e.target.value }))}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        )}
                                    </span>
                                </div>

                                <div className="leaddetail-meta-row">
                                    <span className="leaddetail-meta-label">Source:</span>
                                    <span className="leaddetail-meta-value">
                                        {!editingBasicInfo ? (
                                            basicInfo.source || '—'
                                        ) : (
                                            <select
                                                className="leaddetail-edit-select"
                                                value={basicInfo.source}
                                                onChange={e => setBasicInfo(prev => ({ ...prev, source: e.target.value }))}
                                            >
                                                <option value="">Select Source</option>
                                                <option value="Walk-in">Walk-in</option>
                                                <option value="Referral">Referral</option>
                                                <option value="Social Media">Social Media</option>
                                                <option value="Website">Website</option>
                                                <option value="Campaign">Campaign</option>
                                                <option value="Existing Member">Existing Member</option>
                                                <option value="WhatsApp">WhatsApp</option>
                                            </select>
                                        )}
                                    </span>
                                </div>

                                <div className="leaddetail-meta-row">
                                    <span className="leaddetail-meta-label">Assigned To:</span>
                                    <span className="leaddetail-meta-value">
                                        {!editingBasicInfo ? (
                                            basicInfo.assignedTo || 'Unassigned'
                                        ) : (
                                            <select
                                                className="leaddetail-edit-select"
                                                value={
                                                    !basicInfo.assignedTo ||
                                                    basicInfo.assignedTo === 'Unassigned'
                                                        ? ''
                                                        : basicInfo.assignedTo
                                                }
                                                disabled={staffListLoading}
                                                onChange={(e) =>
                                                    setBasicInfo((prev) => ({
                                                        ...prev,
                                                        assignedTo:
                                                            e.target.value === ''
                                                                ? 'Unassigned'
                                                                : e.target.value
                                                    }))
                                                }
                                            >
                                                <option value="">
                                                    {staffListLoading ? 'Loading staff…' : 'Unassigned'}
                                                </option>
                                                {assignedStaffSelectOptions.map((name) => (
                                                    <option key={name} value={name}>
                                                        {name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </span>
                                </div>

                                <div className="leaddetail-meta-row">
                                    <span className="leaddetail-meta-label">Created:</span>
                                    <span className="leaddetail-meta-value">
                                        {lead?.createdAt
                                            ? new Date(lead.createdAt).toLocaleDateString('en-CA')
                                            : '—'}
                                    </span>
                                </div>
                            </div>

                            {editingBasicInfo && (
                                <div className="leaddetail-prefs-actions" style={{ marginTop: '12px' }}>
                                    <button className="leaddetail-save-btn" onClick={handleSaveBasicInfo}>
                                        Save Changes
                                    </button>
                                    <button
                                        className="leaddetail-cancel-btn"
                                        onClick={() => {
                                            setEditingBasicInfo(false);
                                            setBasicInfo({
                                                name: lead?.name || '',
                                                age: lead?.age || '',
                                                leadNumber: lead?.leadNumber || lead?.leadId || '',
                                                gender: lead?.gender || '',
                                                source: lead?.source || '',
                                                assignedTo: lead?.assignedTo || '',
                                            });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Actions card */}
                        <div className="leaddetail-card leaddetail-actions-card">
                            <div className="leaddetail-card-title">Actions</div>

                            <div className="leaddetail-field-group">
                                <label className="leaddetail-field-label">Change Status:</label>
                                {isLinkedGymMember ? (
                                    <>
                                        <div className="leaddetail-status-readonly">{status}</div>
                                        <p className="leaddetail-field-hint">
                                            Status is fixed for contacts already linked to a gym member (MemberView).
                                        </p>
                                    </>
                                ) : (
                                    <select
                                        className="leaddetail-select"
                                        value={status}
                                        onChange={(e) => handleUpdateActions('status', e.target.value)}
                                    >
                                        <option value="Lead">Lead</option>
                                        <option value="Trial">Trial</option>
                                        <option value="Member">Member</option>
                                    </select>
                                )}
                            </div>

                            <div className="leaddetail-field-group">
                                <label className="leaddetail-field-label">Change Priority:</label>
                                <select
                                    className="leaddetail-select"
                                    value={priority}
                                    onChange={e => handleUpdateActions("priority", e.target.value)}
                                >
                                    <option value="Hot">Hot</option>
                                    <option value="Warm">Warm</option>
                                    <option value="Cold">Cold</option>
                                </select>
                            </div>

                            <div className="leaddetail-dnd-row">
                                <BellOff size={15} />
                                <span>Do Not Disturb</span>
                                <button
                                    className={`leaddetail-toggle ${dnd ? 'leaddetail-toggle-on' : ''}`}
                                    onClick={() => handleUpdateActions("doNotDisturb", !dnd)}
                                >
                                    <span className="leaddetail-toggle-thumb" />
                                </button>
                            </div>

                            <button className="leaddetail-view-quote-btn" onClick={() => setShowQuote(true)}>
                                <FileText size={15} /> View Quote
                            </button>

                            {!isLinkedGymMember && status !== 'Member' && (
                                <button
                                    type="button"
                                    className="leaddetail-convert-member-btn"
                                    onClick={handleConvertToMember}
                                >
                                    Convert to member
                                </button>
                            )}
                        </div>

                        {/* Preferences card */}
                        <div className="leaddetail-card leaddetail-prefs-card">
                            <div className="leaddetail-prefs-header">
                                <div className="leaddetail-card-title">Preferences</div>
                                {!editingPrefs && (
                                    <button className="leaddetail-edit-icon" onClick={() => setEditingPrefs(true)}>
                                        <Edit2 size={14} />
                                    </button>
                                )}
                            </div>

                            {['preferredTime', 'budget', 'timeline'].map(key => (
                                <div key={key} className="leaddetail-pref-field">
                                    <label className="leaddetail-field-label" style={{ textTransform: 'capitalize' }}>
                                        {key === 'preferredTime' ? 'Preferred Time' : key.charAt(0).toUpperCase() + key.slice(1)}
                                    </label>
                                    {editingPrefs
                                        ? <input
                                            className="leaddetail-pref-input"
                                            value={prefs[key]}
                                            onChange={e => setPrefs(p => ({ ...p, [key]: e.target.value }))}
                                          />
                                        : <div className="leaddetail-pref-value">{prefs[key] || '—'}</div>
                                    }
                                </div>
                            ))}

                            <div className="leaddetail-pref-field">
                                <label className="leaddetail-field-label">Address</label>
                                {editingPrefs
                                    ? <textarea
                                        className="leaddetail-pref-textarea"
                                        value={prefs.address}
                                        onChange={e => setPrefs(p => ({ ...p, address: e.target.value }))}
                                        rows={2}
                                      />
                                    : <div className="leaddetail-pref-value">{prefs.address || '—'}</div>
                                }
                            </div>

                            {editingPrefs && (
                                <div className="leaddetail-prefs-actions">
                                    <button className="leaddetail-save-btn" onClick={handleSavePrefs}>Save</button>
                                    <button className="leaddetail-cancel-btn" onClick={() => setEditingPrefs(false)}>Cancel</button>
                                </div>
                            )}
                        </div>

                        {/* Delete — only for prospects, not linked gym members */}
                        {!isLinkedGymMember && (
                            <div className="leaddetail-card leaddetail-delete-card">
                                <button className="leaddetail-delete-btn" onClick={handleDelete}>
                                    <Trash2 size={14} /> Delete Lead
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showLocationModal && (
                <LeadShareLocationModal
                    onClose={() => setShowLocationModal(false)}
                    onConfirm={(draft) => {
                        setLocationDraft(draft);
                        setShowLocationModal(false);
                    }}
                />
            )}

            {imageLightbox && (
                <div
                    className="leaddetail-image-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Image preview"
                    onClick={closeImageLightbox}
                >
                    <div
                        className="leaddetail-image-lightbox-panel"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="leaddetail-image-lightbox-toolbar">
                            <button
                                type="button"
                                className="leaddetail-lightbox-tool"
                                onClick={() =>
                                    setLightboxZoom((z) => Math.max(0.25, Math.round((z - 0.25) * 100) / 100))
                                }
                                aria-label="Zoom out"
                            >
                                <ZoomOut size={20} />
                            </button>
                            <span className="leaddetail-lightbox-zoom-label">
                                {Math.round(lightboxZoom * 100)}%
                            </span>
                            <button
                                type="button"
                                className="leaddetail-lightbox-tool"
                                onClick={() =>
                                    setLightboxZoom((z) => Math.min(5, Math.round((z + 0.25) * 100) / 100))
                                }
                                aria-label="Zoom in"
                            >
                                <ZoomIn size={20} />
                            </button>
                            <button
                                type="button"
                                className="leaddetail-lightbox-tool leaddetail-lightbox-tool-text"
                                onClick={() => setLightboxZoom(1)}
                                aria-label="Reset zoom to 100 percent"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                className="leaddetail-lightbox-tool leaddetail-lightbox-close"
                                onClick={closeImageLightbox}
                                aria-label="Close"
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <div className="leaddetail-image-lightbox-scroll">
                            <img
                                src={imageLightbox.src}
                                alt=""
                                className="leaddetail-image-lightbox-img"
                                onLoad={(e) => {
                                    const el = e.target;
                                    setImageLightbox((prev) =>
                                        prev && prev.src === el.currentSrc
                                            ? {
                                                  ...prev,
                                                  w: el.naturalWidth,
                                                  h: el.naturalHeight
                                              }
                                            : prev
                                    );
                                }}
                                style={
                                    imageLightbox.w && imageLightbox.h
                                        ? {
                                              width: imageLightbox.w * lightboxZoom,
                                              height: imageLightbox.h * lightboxZoom,
                                              maxWidth: 'none',
                                              maxHeight: 'none'
                                          }
                                        : {
                                              maxWidth: 'min(90vw, 100%)',
                                              maxHeight: '78vh',
                                              width: 'auto',
                                              height: 'auto'
                                          }
                                }
                            />
                        </div>
                    </div>
                </div>
            )}

            {showQuote && lead && (
                <QuoteModal
                    lead={lead}
                    gymId={gymId}
                    onClose={() => setShowQuote(false)}
                    onSent={() => setShowQuote(false)}
                />
            )}
        </div>
    );
};

export default LeadDetail;